import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Axios client using Bearer token auth (NOT cookies).
// Rationale: the Kubernetes ingress adds `Access-Control-Allow-Origin: *`
// which browsers reject when combined with credentials. Using Bearer tokens
// works both in web and Capacitor native builds (where cookies don't cross
// scheme boundaries).
export const api = axios.create({
  baseURL: API,
  withCredentials: false,
});

// Inject Authorization: Bearer <token> on every request.
api.interceptors.request.use((config) => {
  const token = getSessionToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global 401 handler: expired or deleted guest session → clear and redirect to landing.
// Public pages that handle their own auth (like invite links) are excluded from the redirect.
const PUBLIC_PATHS = ['/join/'];
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      clearSession();
      const path = window.location.pathname;
      const isPublicPage = PUBLIC_PATHS.some((p) => path.startsWith(p));
      if (path !== '/' && !isPublicPage) {
        window.location.replace('/');
      }
    }
    return Promise.reject(err);
  }
);

export const wsUrl = (roomId, sessionToken) => {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/rooms/${roomId}${sessionToken ? `?token=${sessionToken}` : ""}`;
};

export const wsMatchmakingUrl = (sessionToken) => {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws/matchmaking${sessionToken ? `?token=${sessionToken}` : ""}`;
};

// Session storage strategy:
// - Primary: sessionStorage (per-tab isolation — prevents multi-tab auth clashes
//   when testing 2 players on the same browser).
// - Fallback on read: localStorage (so a fresh tab after OAuth or a reload in
//   the same browser inherits the latest session without requiring re-login).
// - On write: we populate both. On read: sessionStorage wins and we pin it
//   from localStorage the first time, freezing the tab to its own token.
const KEY = "session_token";

export const saveSessionToken = (token) => {
  if (!token) return;
  try { sessionStorage.setItem(KEY, token); } catch {}
  try { localStorage.setItem(KEY, token); } catch {}
};

export function getSessionToken() {
  try {
    const ss = sessionStorage.getItem(KEY);
    if (ss) return ss;
  } catch {}
  try {
    const ls = localStorage.getItem(KEY);
    if (ls) {
      // Pin to this tab so a later login in another tab doesn't hijack us.
      try { sessionStorage.setItem(KEY, ls); } catch {}
      return ls;
    }
  } catch {}
  return null;
}

export const clearSession = () => {
  try { sessionStorage.removeItem(KEY); } catch {}
  try { localStorage.removeItem(KEY); } catch {}
};
